import { Link } from "react-router-dom";
import { Reveal, MaskIn, MaskLine, SectionHead } from "@/components/motion";
import { LogoMark } from "@/components/LogoMark";

const STEPS = [
    {
        t: "Understand",
        d: "We begin by understanding the organisation — its operating environment, challenges, objectives, stakeholders and constraints.",
    },
    {
        t: "Examine",
        d: "We examine the organisation's current position and identify the underlying issues.",
    },
    {
        t: "Analyse",
        d: "We analyse risks, opportunities, gaps and strategic priorities.",
    },
    {
        t: "Develop",
        d: "We develop practical recommendations and solutions suited to the organisation.",
    },
    {
        t: "Implement",
        d: "Where required, we work alongside management to turn recommendations into action.",
    },
    {
        t: "Monitor",
        d: "We help establish mechanisms for monitoring progress and strengthening long-term performance.",
    },
];

const PAIRS = [
    "Purpose with strategy.",
    "Strategy with governance.",
    "Governance with accountability.",
    "Knowledge with execution.",
    "Growth with responsibility.",
];

export default function Approach() {
    return (
        <div>
            <section
                className="mx-auto max-w-7xl px-6 pt-32 md:px-12 md:pt-44"
                data-testid="approach-hero"
            >
                <Reveal y={16}>
                    <p className="eyebrow text-ink/50">Our approach</p>
                </Reveal>
                <h1 className="mt-8 max-w-5xl font-serif-d text-4xl leading-[1.1] sm:text-5xl md:text-7xl">
                    <MaskLine delay={0.2}>
                        We do not simply identify problems.
                    </MaskLine>
                    <MaskLine delay={0.32}>
                        We move organisations from{" "}
                        <em className="italic text-brand">insight to action</em>
                        .
                    </MaskLine>
                </h1>
                <Reveal
                    delay={0.2}
                    className="mt-12 max-w-3xl border-l-2 border-brand pl-8"
                >
                    <p className="text-[15px] leading-relaxed text-ink/60">
                        Business challenges rarely exist in isolation.
                        Regulation affects strategy. Strategy affects
                        operations. Governance affects accountability.
                        Sustainability affects reputation and investment.
                        Leadership affects everything. That is why ELCAYIL
                        takes an integrated approach.
                    </p>
                </Reveal>
            </section>

            <section
                className="mt-24 border-t border-black/10 md:mt-32"
                data-testid="approach-steps"
            >
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <SectionHead index="01" label="How an engagement moves" />
                    <MaskIn className="mt-12 max-w-3xl">
                        <h2 className="font-serif-d text-4xl md:text-6xl">
                            Six deliberate movements.
                        </h2>
                    </MaskIn>
                    <div className="mt-14">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.t} delay={i * 0.05} y={20}>
                                <div
                                    className="group grid grid-cols-12 items-baseline gap-4 border-t border-black/10 py-7 md:py-9"
                                    data-testid={`approach-step-${i + 1}`}
                                >
                                    <span className="col-span-2 font-mono2 text-xs text-brand md:col-span-1">
                                        0{i + 1}
                                    </span>
                                    <span className="row-bg col-span-10 font-serif-d text-2xl transition-colors duration-300 group-hover:text-brand md:col-span-4 md:text-4xl">
                                        {s.t}
                                    </span>
                                    <span className="col-span-10 col-start-3 max-w-xl text-sm leading-relaxed text-ink/60 md:col-span-6 md:col-start-6 md:text-[15px]">
                                        {s.d}
                                    </span>
                                    <span className="hidden text-ink/20 md:col-span-1 md:block md:text-right">
                                        →
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                        <div className="border-t border-black/10" />
                    </div>
                </div>
            </section>

            <section
                className="border-t border-black/10"
                data-testid="approach-philosophy"
            >
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <SectionHead index="02" label="The name ELCAYIL" />
                    <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <MaskIn>
                                <h2 className="font-serif-d text-4xl leading-tight md:text-5xl">
                                    Organisations perform at their best when
                                    they combine:
                                </h2>
                            </MaskIn>
                            <Reveal delay={0.15}>
                                <p className="mt-8 text-[15px] leading-relaxed text-ink/60">
                                    The name ELCAYIL represents our commitment
                                    to professional excellence. This philosophy
                                    underpins every engagement.
                                </p>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7">
                            {PAIRS.map((p, i) => (
                                <Reveal key={p} delay={i * 0.06} y={20}>
                                    <div
                                        className="group border-t border-black/10 py-6 md:py-8"
                                        data-testid={`approach-pair-${i + 1}`}
                                    >
                                        <p className="row-bg font-serif-d text-2xl italic transition-colors duration-300 group-hover:text-brand md:text-4xl">
                                            {p}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                            <div className="border-t border-black/10" />
                            <Reveal delay={0.2}>
                                <p
                                    className="mt-10 font-serif-d text-2xl italic text-brand md:text-3xl"
                                    data-testid="approach-philosophy-closing"
                                >
                                    — Excellent in Performance.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-brand text-white" data-testid="approach-cta">
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <Reveal>
                        <LogoMark variant="white" className="h-12 w-auto" />
                    </Reveal>
                    <MaskIn className="max-w-4xl">
                        <h2 className="font-serif-d text-4xl leading-tight md:text-6xl">
                            See what this approach looks like for your
                            organisation.
                        </h2>
                    </MaskIn>
                    <Reveal
                        delay={0.2}
                        className="mt-10 flex flex-wrap items-center gap-6"
                    >
                        <Link
                            to="/contact"
                            data-testid="approach-cta-contact"
                            className="rounded-full bg-white px-8 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-brandsoft"
                        >
                            Start a Conversation
                        </Link>
                        <p className="text-sm text-white/60">
                            Speak with ELCAYIL CONSULTING LTD. about your
                            organisation, objectives or advisory requirements.
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
